import { PAYOUT_EXAMPLE_BROKERAGE_PERCENT, PAYOUT_EXAMPLE_COMMISSION_AED } from '~/constants/landing';

export type LandingPrices = { memberAnnual: number; proAnnual: number; teamAnnual: number };

export type LandingFacts = {
  /** Highest self-close split in the tier config, or null when the config never loaded. */
  topSelfClosePercent: number | null;
  /** Live catalogue project count, or null when unavailable (never 0). */
  totalProjects: number | null;
  prices: LandingPrices | null;
};

export const EMPTY_LANDING_FACTS: LandingFacts = { topSelfClosePercent: null, totalProjects: null, prices: null };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const positiveNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

// The REFERRAL pseudo-row is not a tier; LEGEND was removed (INT max is Elite 80%).
const PSEUDO_TIER_KEYS = new Set(['REFERRAL', 'LEGEND']);

/** Top self-close % from GET /api/agent/commission-tiers `data` (today's shape or ID-113's). */
export const topSelfClosePercentFrom = (data: unknown): number | null => {
  if (!isRecord(data)) return null;
  const explicit = positiveNumber(data.maxSelfClosePercent);
  if (explicit !== null) return explicit;
  const rows = Array.isArray(data.ladder) ? data.ladder : Array.isArray(data.tiers) ? data.tiers : [];
  const percents = rows
    .filter((row): row is Record<string, unknown> => isRecord(row) && !PSEUDO_TIER_KEYS.has(String(row.tierKey)))
    .map((row) => positiveNumber(row.selfClosePercent))
    .filter((percent): percent is number => percent !== null && percent <= 100);
  return percents.length ? Math.max(...percents) : null;
};

/** `data.stats.totalProjects` from GET /api/developers. */
export const totalProjectsFrom = (data: unknown): number | null => {
  if (!isRecord(data) || !isRecord(data.stats)) return null;
  const total = positiveNumber(data.stats.totalProjects);
  return total === null ? null : Math.round(total);
};

/** Annual USD plan prices from GET /api/billing/prices `data`. */
export const pricesFrom = (data: unknown): LandingPrices | null => {
  if (!isRecord(data) || !isRecord(data.usd)) return null;
  const memberAnnual = positiveNumber(data.usd.memberAnnual);
  const proAnnual = positiveNumber(data.usd.proAnnual);
  const teamAnnual = positiveNumber(data.usd.teamAnnual);
  if (memberAnnual === null || proAnnual === null || teamAnnual === null) return null;
  return { memberAnnual, proAnnual, teamAnnual };
};

export type PayoutExample = {
  commissionAed: number;
  brokeragePercent: number;
  brokerageAed: number;
  insidersPercent: number;
  insidersAed: number;
  deltaAed: number;
};

export const payoutExample = (topPercent: number): PayoutExample => {
  const brokerageAed = Math.round((PAYOUT_EXAMPLE_COMMISSION_AED * PAYOUT_EXAMPLE_BROKERAGE_PERCENT) / 100);
  const insidersAed = Math.round((PAYOUT_EXAMPLE_COMMISSION_AED * topPercent) / 100);
  return {
    commissionAed: PAYOUT_EXAMPLE_COMMISSION_AED,
    brokeragePercent: PAYOUT_EXAMPLE_BROKERAGE_PERCENT,
    brokerageAed,
    insidersPercent: topPercent,
    insidersAed,
    deltaAed: insidersAed - brokerageAed,
  };
};

/** "Starter" style fallback for a tier key without a translation. */
export const titleCaseTierKey = (tierKey: string): string =>
  tierKey
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
